import React, { useImperativeHandle } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { Table, TableAddon, DatePicker } from 'tea-component';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import Calendar, { ICalendarProps } from '../../../components/Calendar';
import useMapping from '@gp/hooks/useMapping';
import { getRefreshedCalendarsData } from '../../_hooks/useOperateDPSReport';
import { getCalendarMilestones } from '../../services';
import { getFirstDayOfMonth, getLastDayOfMonth } from '@gp/utils/common';
import { generateMilestonesMapping } from './utils';
import { DataType } from '@gp/enums/common';
import { TABLE_WIDTH_IN_SMALL_SCREEN, SMALL_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN } from '../../../constants';
import {
  PRIORITY_COLUMN_WIDTH_IN_SMALL_SCREEN,
  PRIORITY_COLUMN_WIDTH,
  MILESTONE_COLUMN_WIDTH_IN_SMALL_SCREEN,
  MILESTONE_DATE_COLUMN_WIDTH_IN_SMALL_SCREEN,
  MILESTONE_COLUMN_WIDTH,
  MILESTONE_DATE_COLUMN_WIDTH,
} from '../../../components/Calendar/constants';

import type { IBaseProps } from '@home/types/common';
import type { IRecord, IMilestoneSimpleDTO } from '@gp/types/global';
import type { IDPSReportCalendarVO } from '@gp/types/dpsReport/common';
import type { IDPSReportModuleInstance } from '../../types';

export type ICalendarV3Instance = IDPSReportModuleInstance

interface ICalendarV3Props extends IBaseProps, Omit<ICalendarProps, 'value' | 'onChange'> {
  // 标题
  title?: string;
  onTitleChange?: (payload: string) => void;
  // 点击Refresh Data按钮的回调
  onRefreshData?: () => void;
  value: {
    start_date: string;
    end_date: string;
    list: IDPSReportCalendarVO[];
  };
  onChange?: (payload: { start_date: string; end_date: string; list: IDPSReportCalendarVO[]; }) => void;
}

const { RangePicker } = DatePicker;

const { injectable } = Table.addons;

const PROJECT_COLUMN_WIDTH_IN_SMALL_SCREEN =
  SMALL_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN - PRIORITY_COLUMN_WIDTH_IN_SMALL_SCREEN;

const STATUS_COLUMN_WIDTH_IN_SMALL_SCREEN = 180;

const COMMON_COLUMN_WIDTH_IN_SMALL_SCREEN =
  (TABLE_WIDTH_IN_SMALL_SCREEN -
    (PRIORITY_COLUMN_WIDTH_IN_SMALL_SCREEN +
      PROJECT_COLUMN_WIDTH_IN_SMALL_SCREEN +
      MILESTONE_COLUMN_WIDTH_IN_SMALL_SCREEN +
      MILESTONE_DATE_COLUMN_WIDTH_IN_SMALL_SCREEN +
      STATUS_COLUMN_WIDTH_IN_SMALL_SCREEN)) /
  2;

const PROJECT_COLUMN_WIDTH = `${(PROJECT_COLUMN_WIDTH_IN_SMALL_SCREEN / TABLE_WIDTH_IN_SMALL_SCREEN) * 100 - 2}%`;

const STATUS_COLUMN_WIDTH = `${(STATUS_COLUMN_WIDTH_IN_SMALL_SCREEN / TABLE_WIDTH_IN_SMALL_SCREEN) * 100 + 8}%`;

const COMMON_COLUMN_WIDTH = `${(COMMON_COLUMN_WIDTH_IN_SMALL_SCREEN / TABLE_WIDTH_IN_SMALL_SCREEN) * 100}%`;

const COLUMN_WIDTH_MAPPING: IRecord<string | undefined> = {
  priority: PRIORITY_COLUMN_WIDTH,
  project_id: PROJECT_COLUMN_WIDTH,
  business_owner: COMMON_COLUMN_WIDTH,
  pteam: COMMON_COLUMN_WIDTH,
  milestone_id: MILESTONE_COLUMN_WIDTH,
  date: MILESTONE_DATE_COLUMN_WIDTH,
  status: STATUS_COLUMN_WIDTH,
};

const getRangePickerValue = (payload: {
  start_date: string;
  end_date: string;
  list: IDPSReportCalendarVO[];
}): [moment.Moment, moment.Moment] | undefined => {
  const { start_date: startDate, end_date: endDate } = payload || {};
  if (!startDate || !endDate) return undefined;
  return [moment(startDate).startOf('month'), moment(endDate).endOf('month')];
};

const CARD_TITLE = 'Calendar';

const CalendarV3: React.ForwardRefRenderFunction<ICalendarV3Instance, ICalendarV3Props> = (props, ref) => {
  const { isLoading, setIsInnerLoading, title, onTitleChange, projects, onRefreshData, value, onChange, ...restProps } =
    props;

  const addons: TableAddon<IDPSReportCalendarVO>[] = [
    injectable({
      row: (props, context) => {
        const { className } = props || {};
        const { record } = context || {};
        const { data_type: dataType } = record || {};
        return {
          ...props,
          className: classnames(className, {
            'dbg-table-tr-highlight': dataType === DataType.AutoFetched,
          }),
        };
      },
    }),
  ];

  const [milestonesMapping, { set: setMilestonesMapping, setAll: setAllMilestonesMapping }] = useMapping<
  IMilestoneSimpleDTO[]
  >({});

  useImperativeHandle(ref, () => ({
    initialize: async (payload) => {
      const { calendar } = payload || {};
      const initialMilestonesMapping = await generateMilestonesMapping(calendar);
      setAllMilestonesMapping(initialMilestonesMapping);
    },
  }));

  return (
    <Calendar
      {...restProps}
      isMilestoneKeepEditable
      isSupportRenameTitle
      columnWidthMapping={COLUMN_WIDTH_MAPPING}
      isLoading={isLoading}
      // title={title}
      title={title || CARD_TITLE}
      onTitleChange={onTitleChange}
      addons={addons}
      projects={projects}
      milestonesMapping={milestonesMapping}
      value={value?.list}
      onRefreshData={onRefreshData}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onProjectChange={async (payload) => {
        const milestones = await getCalendarMilestones({
          project_id: payload ? [payload] : [],
          start_date: getFirstDayOfMonth(value?.start_date),
          end_date: getLastDayOfMonth(value?.end_date),
        });
        setMilestonesMapping(payload, milestones);
      }}
      onChange={(payload) => {
        onChange?.({ ...value, list: payload });
      }}
      actions={
        <DatePickerWrapper
          style={{ marginTop: isLoading ? 16 : 0, marginBottom: 20 }}
          className="dbg-date-picker-wrapper--month"
          isRange
          label="Month"
        >
          <RangePicker
            calendarType="month"
            placeholder={['Select Month', '']}
            value={getRangePickerValue(value)}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onChange={async (payload) => {
              setIsInnerLoading?.(true);
              const [startDateM, endDateM] = payload || [];
              const startDate = startDateM?.format('YYYY-MM-DD') || '';
              const endDate = endDateM?.format('YYYY-MM-DD') || '';
              const valueTmp = { ...value, start_date: startDate, end_date: endDate };
              let listTmp: IDPSReportCalendarVO[] = [];
              onChange?.(valueTmp);
              try {
                const refreshedCalendarsData = await getRefreshedCalendarsData({
                  current: value?.list,
                  start_date: startDate,
                  end_date: endDate,
                });
                listTmp = refreshedCalendarsData || [];
              } catch (exception) {
                listTmp = [];
              } finally {
                setIsInnerLoading?.(false);
              }
              onChange?.({ ...valueTmp, list: listTmp });
            }}
          />
        </DatePickerWrapper>
      }
    />
  );
};

// @ts-ignore
export default React.forwardRef(CalendarV3);
